import { useState } from "react"
import { signInWithEmailAndPassword, sendPasswordResetEmail, signInWithPopup, GoogleAuthProvider } from "firebase/auth"
import { doc, getDoc, setDoc } from "firebase/firestore"
import { auth, db } from "../services/firebase"
import { Link, useNavigate } from "react-router-dom"
import "./Login.css"

const googleProvider = new GoogleAuthProvider()

function traducirError(codigo) {
  const errores = {
    "auth/user-not-found":      "No existe una cuenta con ese email.",
    "auth/wrong-password":      "Contraseña incorrecta.",
    "auth/invalid-email":       "El email no es válido.",
    "auth/too-many-requests":   "Demasiados intentos. Intentá más tarde.",
    "auth/invalid-credential":  "Email o contraseña incorrectos.",
    "auth/popup-closed-by-user":"Cerraste la ventana de Google. Intentá de nuevo.",
  }
  return errores[codigo] || "Ocurrió un error. Intentá de nuevo."
}

// Ícono ojo
function OjoIcon({ visible }) {
  return visible ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  )
}

// Ícono Google
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  )
}

export default function Login() {
  const [email, setEmail]             = useState("")
  const [password, setPassword]       = useState("")
  const [verPass, setVerPass]         = useState(false)
  const [error, setError]             = useState("")
  const [loading, setLoading]         = useState(false)
  const [loadingGoogle, setLoadingGoogle] = useState(false)
  const [resetEnviado, setResetEnviado]   = useState(false)
  const [resetLoading, setResetLoading]   = useState(false)
  const navigate = useNavigate()

  async function handleLogin(e) {
    e.preventDefault()
    setError("")
    setLoading(true)
    try {
      await signInWithEmailAndPassword(auth, email, password)
      navigate("/dashboard")
    } catch (err) {
      setError(traducirError(err.code))
    } finally {
      setLoading(false)
    }
  }

  async function handleGoogle() {
    setError("")
    setLoadingGoogle(true)
    try {
      const result = await signInWithPopup(auth, googleProvider)
      const user = result.user

      // Si es usuario nuevo → crear doc en usuarios_web
      const ref  = doc(db, "usuarios_web", user.uid)
      const snap = await getDoc(ref)
      if (!snap.exists()) {
        const [nombre, ...resto] = (user.displayName || "").split(" ")
        await setDoc(ref, {
          email:               user.email,
          nombre:              nombre || "",
          apellido:            resto.join(" ") || "",
          es_admin:            false,
          id_cliente_asociado: null,
          creado_en:           new Date(),
          proveedor:           "google",
        })
        if (typeof window !== "undefined" && window.fbq) {
          window.fbq("track", "CompleteRegistration", { content_name: "registro_google" })
        }
      }
      navigate("/dashboard")
    } catch (err) {
      setError(traducirError(err.code))
    } finally {
      setLoadingGoogle(false)
    }
  }

  async function handleRecuperar() {
    if (!email) return setError("Ingresá tu email primero para recuperar la contraseña.")
    setResetLoading(true)
    setError("")
    try {
      await sendPasswordResetEmail(auth, email)
      setResetEnviado(true)
    } catch (err) {
      setError(traducirError(err.code))
    } finally {
      setResetLoading(false)
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-card__icono">✈️</div>
        <h1 className="login-card__titulo">Iniciar sesión</h1>
        <p className="login-card__sub">Accedé a tu panel de viajes</p>

        {resetEnviado ? (
          <div className="login-reset-ok">
            <div className="login-reset-ok__icono">📧</div>
            <div className="login-reset-ok__titulo">Email enviado</div>
            <div className="login-reset-ok__sub">
              Revisá tu casilla y seguí las instrucciones para restablecer tu contraseña.
            </div>
            <button className="login-reset-ok__volver" onClick={() => setResetEnviado(false)}>
              Volver al login
            </button>
          </div>
        ) : (
          <>
            {/* Botón Google */}
            <button
              className="auth-btn-google"
              onClick={handleGoogle}
              disabled={loadingGoogle || loading}
              type="button"
            >
              <GoogleIcon />
              {loadingGoogle ? "Conectando..." : "Continuar con Google"}
            </button>

            <div className="auth-divider">
              <span>o ingresá con email</span>
            </div>

            <form onSubmit={handleLogin} className="login-form">
              <div className="login-form__group">
                <label className="login-form__label">Email</label>
                <input
                  type="email"
                  className="login-form__input"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="login-form__group">
                <label className="login-form__label">Contraseña</label>
                <div className="input-pass-wrap">
                  <input
                    type={verPass ? "text" : "password"}
                    className="login-form__input"
                    placeholder="Tu contraseña"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="input-pass-ojo"
                    onClick={() => setVerPass(v => !v)}
                    tabIndex={-1}
                    title={verPass ? "Ocultar contraseña" : "Ver contraseña"}
                  >
                    <OjoIcon visible={verPass} />
                  </button>
                </div>
              </div>

              <div style={{ textAlign: "right", marginTop: "-8px", marginBottom: "8px" }}>
                <button
                  type="button"
                  onClick={handleRecuperar}
                  disabled={resetLoading}
                  className="login-link-btn"
                >
                  {resetLoading ? "Enviando..." : "Olvidé mi contraseña"}
                </button>
              </div>

              {error && <div className="login-form__error">{error}</div>}

              <button type="submit" className="login-form__btn" disabled={loading || loadingGoogle}>
                {loading ? "Ingresando..." : "Ingresar"}
              </button>
            </form>
          </>
        )}

        <div className="login-card__footer">
          ¿No tenés cuenta?{" "}
          <Link to="/register" className="login-card__link">Registrate</Link>
        </div>
        <div style={{ textAlign: "center", marginTop: "8px" }}>
          <Link to="/" className="login-card__link" style={{ fontSize: "0.8rem", color: "#9ca3af" }}>
            Volver al inicio
          </Link>
        </div>
      </div>
    </div>
  )
}